/**
 * O&M layout (operation & maintenance): maintenance / fire walkways, roof safety (lifelines, anchors, guardrails,
 * hazard zones), access points & equipment service zones, cleaning (water points, wash pipes, robot docks) and an
 * inspection checklist. Plan coordinates in mm.
 *
 * The checks are GEOMETRIC planning aids (reach, coverage, connectivity, clearance). Default distances are illustrative
 * project settings, NOT values taken from a fire code or safety regulation — the engineer sets them per project.
 */
import { z } from 'zod';
import type { EdgeKind } from './array-layout';
import {
  pointInPolygon,
  pointSegmentDistance,
  polygonsOverlap,
  segmentsIntersect,
  type Vec,
} from './geometry';
import type { RuleLine } from './parallel';

export const OM_WALKWAY_KINDS = ['MAINTENANCE', 'FIRE', 'ACCESS'] as const;
export type OmWalkwayKind = (typeof OM_WALKWAY_KINDS)[number];
export const OM_WALKWAY_LABEL: Record<OmWalkwayKind, string> = {
  MAINTENANCE: 'Lối đi bảo trì',
  FIRE: 'Lối đi PCCC / thoát nạn',
  ACCESS: 'Tuyến tiếp cận thiết bị',
};
export const OM_ACCESS_KINDS = ['LADDER', 'HATCH', 'STAIR', 'HOIST'] as const;
export type OmAccessKind = (typeof OM_ACCESS_KINDS)[number];
export const OM_ACCESS_LABEL: Record<OmAccessKind, string> = {
  LADDER: 'Thang lên mái',
  HATCH: 'Cửa lên mái',
  STAIR: 'Cầu thang',
  HOIST: 'Điểm cẩu / tời vật tư',
};
export const OM_DOCK_KINDS = ['ROBOT', 'CART'] as const;
export type OmDockKind = (typeof OM_DOCK_KINDS)[number];
export const OM_DOCK_LABEL: Record<OmDockKind, string> = {
  ROBOT: 'Trạm đỗ robot vệ sinh',
  CART: 'Vị trí đặt thiết bị / xe vệ sinh',
};

const pt = z.tuple([z.number().finite(), z.number().finite()]);
const id = z.string().min(1).max(40);
const label = z.string().max(120).optional();

export const omSettingsSchema = z
  .object({
    /** Narrowest acceptable maintenance walkway (project setting) */
    minWalkwayMm: z.number().min(100).max(5000).default(600),
    /** Narrowest acceptable fire / escape walkway (project setting) */
    fireWalkwayMm: z.number().min(100).max(5000).default(1000),
    /** Farthest a panel may be from a walkway or perimeter access band */
    maxReachMm: z.number().min(500).max(50_000).default(4500),
    /** Band along unprotected roof edges treated as a fall hazard */
    edgeHazardMm: z.number().min(0).max(10_000).default(2000),
    /** Perimeter setbacks at least `minWalkwayMm` wide count as access */
    perimeterAccess: z.boolean().default(true),
  })
  .loose();

export const omDataSchema = z
  .object({
    version: z.number().int().min(1),
    unit: z.literal('mm'),
    walkways: z
      .array(
        z
          .object({
            id,
            kind: z.enum(OM_WALKWAY_KINDS),
            points: z.array(pt).min(2).max(500),
            widthMm: z.number().min(50).max(10_000),
            label,
          })
          .loose(),
      )
      .max(1000)
      .default([]),
    lifelines: z
      .array(
        z
          .object({
            id,
            points: z.array(pt).min(2).max(500),
            anchorSpacingMm: z.number().min(500).max(50_000).default(10_000),
            label,
          })
          .loose(),
      )
      .max(500)
      .default([]),
    anchors: z
      .array(z.object({ id, at: pt, label }).loose())
      .max(2000)
      .default([]),
    guardrails: z
      .array(
        z
          .object({
            id,
            points: z.array(pt).min(2).max(500),
            heightMm: z.number().min(300).max(3000).default(1100),
            label,
          })
          .loose(),
      )
      .max(500)
      .default([]),
    hazards: z
      .array(z.object({ id, points: z.array(pt).min(3).max(500), label }).loose())
      .max(500)
      .default([]),
    access: z
      .array(z.object({ id, kind: z.enum(OM_ACCESS_KINDS), at: pt, label }).loose())
      .max(500)
      .default([]),
    water: z
      .array(
        z.object({ id, at: pt, reachMm: z.number().min(500).max(500_000).default(30_000), label }).loose(),
      )
      .max(500)
      .default([]),
    pipes: z
      .array(
        z
          .object({
            id,
            points: z.array(pt).min(2).max(500),
            dnMm: z.number().min(6).max(300).default(25),
            label,
          })
          .loose(),
      )
      .max(500)
      .default([]),
    docks: z
      .array(z.object({ id, kind: z.enum(OM_DOCK_KINDS), at: pt, label }).loose())
      .max(500)
      .default([]),
    settings: omSettingsSchema.default(() => omSettingsSchema.parse({})),
    checklist: z
      .array(
        z
          .object({
            id,
            task: z.string().trim().min(1).max(200),
            frequency: z.string().trim().max(80).default(''),
            scope: z.string().trim().max(200).default(''),
            note: z.string().max(500).optional(),
          })
          .loose(),
      )
      .max(200)
      .default([]),
  })
  .loose();
export type OmData = z.infer<typeof omDataSchema>;

/** Starting checklist — illustrative; adjust to the O&M contract and the manufacturers' manuals. */
export const DEFAULT_OM_CHECKLIST: OmData['checklist'] = [
  {
    id: 'K1',
    task: 'Vệ sinh bề mặt tấm pin',
    frequency: 'Theo mức bám bẩn thực tế',
    scope: 'Toàn bộ dãy tấm',
    note: 'Tham khảo — điều chỉnh theo hợp đồng O&M.',
  },
  {
    id: 'K2',
    task: 'Kiểm tra trực quan tấm pin, khung, kẹp',
    frequency: 'Định kỳ',
    scope: 'Toàn bộ dãy tấm',
  },
  {
    id: 'K3',
    task: 'Kiểm tra siết bu lông, kẹp, chân đỡ / bản mã',
    frequency: 'Định kỳ',
    scope: 'Kết cấu đỡ',
  },
  { id: 'K4', task: 'Kiểm tra cáp DC, đầu nối MC4, máng cáp', frequency: 'Định kỳ', scope: 'Tuyến cáp' },
  { id: 'K5', task: 'Chụp ảnh nhiệt phát hiện điểm nóng', frequency: 'Định kỳ', scope: 'Toàn bộ dãy tấm' },
  {
    id: 'K6',
    task: 'Kiểm tra biến tần, tủ điện, nối đất, chống sét',
    frequency: 'Định kỳ',
    scope: 'Thiết bị điện',
  },
  {
    id: 'K7',
    task: 'Kiểm tra dây cứu sinh, điểm neo, lan can, thang',
    frequency: 'Trước mỗi lần làm việc trên mái & định kỳ',
    scope: 'Thiết bị an toàn',
  },
];

export const emptyOm = (): OmData =>
  omDataSchema.parse({ version: 1, unit: 'mm', checklist: DEFAULT_OM_CHECKLIST });

const V = (p: [number, number]): Vec => ({ x: p[0], y: p[1] });
const polyLen = (pts: [number, number][]) =>
  pts.slice(1).reduce((t, p, i) => t + Math.hypot(p[0] - pts[i]![0], p[1] - pts[i]![1]), 0);

/** Anchor positions of a lifeline: both ends + intermediates so no span exceeds `spacingMm`. */
export function lifelineAnchors(points: [number, number][], spacingMm: number): [number, number][] {
  const out: [number, number][] = [points[0]!];
  for (let i = 0; i + 1 < points.length; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const n = Math.max(1, Math.ceil(L / Math.max(1, spacingMm) - 1e-9));
    for (let k = 1; k <= n; k++) out.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
  }
  return out;
}

export interface OmQuantities {
  walkways: { kind: OmWalkwayKind; lengthM: number; areaM2: number; count: number }[];
  lifelineM: number;
  lifelineAnchors: number;
  manualAnchors: number;
  guardrailM: number;
  pipes: { dnMm: number; lengthM: number }[];
  waterPoints: number;
  access: Partial<Record<OmAccessKind, number>>;
  docks: Partial<Record<OmDockKind, number>>;
}

const r2 = (x: number) => Math.round(x * 100) / 100;

export function omQuantities(om: OmData): OmQuantities {
  const walk = new Map<OmWalkwayKind, { lengthM: number; areaM2: number; count: number }>();
  for (const w of om.walkways) {
    const L = polyLen(w.points) / 1000;
    const e = walk.get(w.kind) ?? { lengthM: 0, areaM2: 0, count: 0 };
    e.lengthM += L;
    e.areaM2 += (L * w.widthMm) / 1000;
    e.count++;
    walk.set(w.kind, e);
  }
  const pipes = new Map<number, number>();
  for (const p of om.pipes) pipes.set(p.dnMm, (pipes.get(p.dnMm) ?? 0) + polyLen(p.points) / 1000);
  const count = <K extends string>(list: { kind: K }[]) =>
    list.reduce<Partial<Record<K, number>>>((t, x) => ({ ...t, [x.kind]: (t[x.kind] ?? 0) + 1 }), {});
  return {
    walkways: [...walk].map(([kind, e]) => ({
      kind,
      lengthM: r2(e.lengthM),
      areaM2: r2(e.areaM2),
      count: e.count,
    })),
    lifelineM: r2(om.lifelines.reduce((t, l) => t + polyLen(l.points), 0) / 1000),
    lifelineAnchors: om.lifelines.reduce(
      (t, l) => t + lifelineAnchors(l.points, l.anchorSpacingMm).length,
      0,
    ),
    manualAnchors: om.anchors.length,
    guardrailM: r2(om.guardrails.reduce((t, l) => t + polyLen(l.points), 0) / 1000),
    pipes: [...pipes].map(([dnMm, m]) => ({ dnMm, lengthM: r2(m) })).sort((a, b) => a.dnMm - b.dnMm),
    waterPoints: om.water.length,
    access: count(om.access),
    docks: count(om.docks),
  };
}

/** BOQ lines for the modelled O&M items (category OTHER, rules awaiting approval). */
export function omRuleLines(q: OmQuantities): RuleLine[] {
  const lines: RuleLine[] = [];
  const add = (key: string, label: string, unit: string, quantity: number, basis: string) => {
    if (quantity > 0)
      lines.push({
        key: `OM:${key}`,
        category: 'OTHER',
        ruleId: 'OM.LAYOUT@v1',
        productId: null,
        label,
        unit,
        quantity,
        materialKey: `OM_${key.split(':')[0]}`,
        basis: [{ label: 'Đo từ bản bố trí O&M', value: basis }],
        unverified: true,
      });
  };
  for (const w of q.walkways)
    add(
      `WALKWAY:${w.kind}`,
      `${OM_WALKWAY_LABEL[w.kind]} (diện tích lối đi)`,
      'm²',
      w.areaM2,
      `${w.count} tuyến, dài ${w.lengthM.toLocaleString('vi-VN')} m`,
    );
  add(
    'LIFELINE',
    'Dây cứu sinh (chiều dài tuyến)',
    'mét',
    q.lifelineM,
    `${q.lifelineM.toLocaleString('vi-VN')} m`,
  );
  add(
    'ANCHOR',
    'Điểm neo dây cứu sinh / điểm neo an toàn',
    'cái',
    q.lifelineAnchors + q.manualAnchors,
    `${q.lifelineAnchors} trên tuyến dây + ${q.manualAnchors} điểm neo riêng`,
  );
  add('GUARDRAIL', 'Lan can mái', 'mét', q.guardrailM, `${q.guardrailM.toLocaleString('vi-VN')} m`);
  for (const p of q.pipes)
    add(
      `PIPE:DN${p.dnMm}`,
      `Ống cấp nước vệ sinh DN${p.dnMm}`,
      'mét',
      p.lengthM,
      `${p.lengthM.toLocaleString('vi-VN')} m theo tuyến`,
    );
  add('WATER_POINT', 'Điểm cấp nước vệ sinh tấm', 'cái', q.waterPoints, `${q.waterPoints} điểm`);
  for (const [k, n] of Object.entries(q.access))
    add(`ACCESS:${k}`, OM_ACCESS_LABEL[k as OmAccessKind], 'cái', n!, `${n} vị trí`);
  for (const [k, n] of Object.entries(q.docks))
    add(`DOCK:${k}`, OM_DOCK_LABEL[k as OmDockKind], 'cái', n!, `${n} vị trí`);
  return lines;
}

// ---------------------------------------------------------------- checks

export interface OmCheckInput {
  om: OmData;
  roofs: { id: string; name: string; outline: Vec[]; edgeKinds: EdgeKind[]; setbacksMm: number[] }[];
  panels: { id: string; roofId: string; corners: Vec[] }[];
  equipment: { id: string; label: string; footprint: Vec[]; zone: Vec[] }[];
  obstacles: { id: string; label: string; kind: string; outline: Vec[] }[];
}

export interface OmIssue {
  severity: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  /** Ids of the objects involved (walkways, panels, equipment…) */
  ids?: string[];
}

function distPolyPoly(p: Vec[], q: Vec[], closedP: boolean, closedQ: boolean): number {
  const segs = (a: Vec[], closed: boolean) =>
    (closed ? a : a.slice(0, -1)).map((v, i) => [v, a[(i + 1) % a.length]!] as const);
  const sp = segs(p, closedP);
  const sq = segs(q, closedQ);
  let d = Infinity;
  for (const [a, b] of sp)
    for (const [c, e] of sq) {
      if (segmentsIntersect(a, b, c, e)) return 0;
      d = Math.min(
        d,
        pointSegmentDistance(a, c, e),
        pointSegmentDistance(b, c, e),
        pointSegmentDistance(c, a, b),
        pointSegmentDistance(e, a, b),
      );
    }
  if (closedQ && p.some((v) => pointInPolygon(v, q))) return 0;
  if (closedP && q.some((v) => pointInPolygon(v, p))) return 0;
  return d;
}

const ids = (l: string[], n = 20) => (l.length > n ? [...l.slice(0, n)] : l);

/** Unprotected roof edges (no parapet, no guardrail along most of the edge). */
function unprotectedEdges(input: OmCheckInput) {
  const out: { roof: string; a: Vec; b: Vec }[] = [];
  for (const r of input.roofs)
    r.outline.forEach((a, i) => {
      const kind = r.edgeKinds[i] ?? 'OTHER';
      if (kind === 'PARAPET' || kind === 'RIDGE' || kind === 'HIP' || kind === 'VALLEY') return;
      const b = r.outline[(i + 1) % r.outline.length]!;
      const L = Math.hypot(b.x - a.x, b.y - a.y);
      if (L < 1) return;
      // Guardrail coverage: sample the edge every 250 mm
      const n = Math.max(2, Math.ceil(L / 250));
      let covered = 0;
      for (let k = 0; k <= n; k++) {
        const p = { x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n };
        if (
          input.om.guardrails.some((g) =>
            g.points.slice(1).some((q, j) => pointSegmentDistance(p, V(g.points[j]!), V(q)) <= 400),
          )
        )
          covered++;
      }
      if (covered / (n + 1) < 0.8) out.push({ roof: r.name, a, b });
    });
  return out;
}

export function omChecks(input: OmCheckInput): OmIssue[] {
  const { om } = input;
  const s = om.settings;
  const out: OmIssue[] = [];
  const walks = om.walkways.map((w) => ({ w, pts: w.points.map(V), half: w.widthMm / 2 }));

  // Walkway widths (project settings)
  const narrow = om.walkways.filter(
    (w) => w.widthMm < (w.kind === 'FIRE' ? s.fireWalkwayMm : s.minWalkwayMm),
  );
  if (narrow.length)
    out.push({
      severity: 'warning',
      code: 'WALKWAY_NARROW',
      message: `${narrow.length} lối đi hẹp hơn mức đã đặt cho dự án (bảo trì ${s.minWalkwayMm} mm, PCCC ${s.fireWalkwayMm} mm).`,
      ids: narrow.map((w) => w.id),
    });

  // Reach: every panel within maxReach of a walkway or a perimeter access band
  const perimeter = s.perimeterAccess
    ? input.roofs.flatMap((r) =>
        r.outline.flatMap((a, i) =>
          (r.setbacksMm[i] ?? 0) >= s.minWalkwayMm
            ? [{ roof: r.id, a, b: r.outline[(i + 1) % r.outline.length]!, band: r.setbacksMm[i]! }]
            : [],
        ),
      )
    : [];
  const unreachable: string[] = [];
  for (const p of input.panels) {
    let ok = false;
    for (const e of perimeter) {
      if (e.roof !== p.roofId) continue;
      if (Math.min(...p.corners.map((c) => pointSegmentDistance(c, e.a, e.b))) <= e.band + s.maxReachMm) {
        ok = true;
        break;
      }
    }
    if (!ok)
      for (const w of walks)
        if (distPolyPoly(p.corners, w.pts, true, false) - w.half <= s.maxReachMm) {
          ok = true;
          break;
        }
    if (!ok) unreachable.push(p.id);
  }
  if (unreachable.length)
    out.push({
      severity: 'warning',
      code: 'PANEL_REACH',
      message: `${unreachable.length} tấm cách lối đi / dải tiếp cận mép mái quá ${s.maxReachMm} mm — khó vệ sinh, bảo trì; cân nhắc thêm lối đi.`,
      ids: ids(unreachable, 200),
    });

  // Fall hazards: unprotected edges + skylights + manual hazard zones
  const edges = unprotectedEdges(input);
  const nearEdge = (pts: Vec[], closed: boolean) =>
    edges.some((e) => distPolyPoly(pts, [e.a, e.b], closed, false) < s.edgeHazardMm);
  const protectedBy = (pts: Vec[]) =>
    om.lifelines.some((l) => distPolyPoly(pts, l.points.map(V), false, false) <= 3000);
  const skylights = input.obstacles.filter((o) => o.kind === 'SKYLIGHT');
  const exposed: string[] = [];
  for (const w of walks) {
    const inHazard =
      (s.edgeHazardMm > 0 && nearEdge(w.pts, false)) ||
      om.hazards.some((h) => distPolyPoly(w.pts, h.points.map(V), false, true) === 0) ||
      skylights.some((k) => distPolyPoly(w.pts, k.outline, false, true) < 1000 + w.half);
    if (inHazard && !protectedBy(w.pts)) exposed.push(w.w.id);
  }
  if (exposed.length)
    out.push({
      severity: 'warning',
      code: 'WALKWAY_HAZARD',
      message: `${exposed.length} lối đi đi qua vùng nguy hiểm (mép mái không lan can, giếng trời, vùng nguy hiểm) mà chưa có dây cứu sinh gần đó.`,
      ids: exposed,
    });
  if (s.edgeHazardMm > 0 && edges.length) {
    const edgePanels = input.panels.filter((p) => nearEdge(p.corners, true)).map((p) => p.id);
    if (edgePanels.length)
      out.push({
        severity: 'info',
        code: 'PANELS_NEAR_EDGE',
        message: `${edgePanels.length} tấm nằm trong dải ${s.edgeHazardMm} mm của mép mái không có lan can — thi công / bảo trì cần dây cứu sinh hoặc lan can.`,
        ids: ids(edgePanels, 200),
      });
  }

  // Access points per roof with panels
  const roofsWithPanels = new Set(input.panels.map((p) => p.roofId));
  const noAccess = input.roofs.filter(
    (r) =>
      roofsWithPanels.has(r.id) &&
      !om.access.some(
        (a) =>
          pointInPolygon(V(a.at), r.outline) ||
          r.outline.some(
            (v, i) => pointSegmentDistance(V(a.at), v, r.outline[(i + 1) % r.outline.length]!) <= 1500,
          ),
      ),
  );
  if (noAccess.length)
    out.push({
      severity: 'warning',
      code: 'NO_ACCESS',
      message: `Chưa có điểm lên mái (thang / cửa mái / cầu thang) cho: ${noAccess.map((r) => r.name).join(', ')}.`,
      ids: noAccess.map((r) => r.id),
    });

  // Walkway networks must reach an access point
  if (walks.length) {
    const parent = walks.map((_, i) => i);
    const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
    for (let i = 0; i < walks.length; i++)
      for (let j = i + 1; j < walks.length; j++)
        if (distPolyPoly(walks[i]!.pts, walks[j]!.pts, false, false) <= walks[i]!.half + walks[j]!.half + 1)
          parent[find(i)] = find(j);
    const linked = new Set<number>();
    walks.forEach((w, i) => {
      if (
        om.access.some((a) =>
          w.pts.slice(1).some((q, k) => pointSegmentDistance(V(a.at), w.pts[k]!, q) <= w.half + 1500),
        )
      )
        linked.add(find(i));
    });
    const lone = walks.filter((_, i) => !linked.has(find(i))).map((w) => w.w.id);
    if (lone.length && om.access.length)
      out.push({
        severity: 'warning',
        code: 'WALKWAY_DISCONNECTED',
        message: `${lone.length} lối đi không nối tới điểm lên mái nào.`,
        ids: lone,
      });
  }

  // Equipment service zones
  for (const e of input.equipment) {
    if (e.zone.length < 3) continue;
    const blockedP = input.panels.filter((p) => polygonsOverlap(p.corners, e.zone)).map((p) => p.id);
    const blockedO = input.obstacles.filter((o) => polygonsOverlap(o.outline, e.zone)).map((o) => o.label);
    if (blockedP.length || blockedO.length)
      out.push({
        severity: 'error',
        code: 'SERVICE_ZONE_BLOCKED',
        message: `Vùng thao tác trước “${e.label}” bị chiếm bởi ${[blockedP.length && `${blockedP.length} tấm pin`, blockedO.length && `vật cản (${blockedO.join(', ')})`].filter(Boolean).join(' và ')}.`,
        ids: [e.id, ...ids(blockedP)],
      });
    if (walks.length && !walks.some((w) => distPolyPoly(e.zone, w.pts, true, false) <= w.half + 300))
      out.push({
        severity: 'warning',
        code: 'EQUIPMENT_NO_ROUTE',
        message: `“${e.label}” chưa có lối đi / tuyến tiếp cận tới vùng thao tác.`,
        ids: [e.id],
      });
  }

  // Cleaning water coverage
  if (input.panels.length) {
    if (!om.water.length)
      out.push({ severity: 'info', code: 'NO_WATER', message: 'Chưa bố trí điểm cấp nước vệ sinh tấm.' });
    else {
      const dry = input.panels
        .filter(
          (p) =>
            !om.water.some((w) =>
              p.corners.every((c) => Math.hypot(c.x - w.at[0], c.y - w.at[1]) <= w.reachMm),
            ),
        )
        .map((p) => p.id);
      if (dry.length)
        out.push({
          severity: 'warning',
          code: 'WATER_REACH',
          message: `${dry.length} tấm ngoài tầm với của các điểm cấp nước (theo bán kính vòi đã nhập).`,
          ids: ids(dry, 200),
        });
    }
  }
  if (!om.checklist.length)
    out.push({
      severity: 'info',
      code: 'NO_CHECKLIST',
      message: 'Chưa có danh mục công việc kiểm tra / bảo trì định kỳ.',
    });
  return out;
}
